import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { toast } from "sonner";
import { Plus, Edit, Trash2, FileText, ArrowRight, ChevronLeft, AlertTriangle, Loader2 } from "lucide-react";

export default function ModulesPage() {
  const { subjectId } = useParams();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ module_title: "", description: "", status: "active" });
  const [deleteDlg, setDeleteDlg] = useState({ open: false, id: null, title: "", busy: false });

  const load = () => api.get(`/modules?subject_id=${subjectId}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [subjectId]);

  const reset = () => { setEditing(null); setForm({ module_title: "", description: "", status: "active" }); };

  const save = async () => {
    try {
      const payload = { ...form, subject_id: subjectId };
      if (editing) { await api.patch(`/modules/${editing.id}`, payload); toast.success("Updated"); }
      else { await api.post("/modules", payload); toast.success("Created"); }
      setOpen(false); reset(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    setDeleteDlg((d) => ({ ...d, busy: true }));
    try {
      await api.delete(`/modules/${id}`);
      toast.success("Module deleted");
      setDeleteDlg({ open: false, id: null, title: "", busy: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete module");
      setDeleteDlg((d) => ({ ...d, busy: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="modules-page">
      <Link to="/admin/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Back to Courses</Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Subject</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Modules</h1>
        </div>
        {hasPerm(user, "module", "add") && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="add-module-btn"><Plus className="w-4 h-4 mr-2" />New Module</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Module" : "New Module"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Module title</Label><Input value={form.module_title} onChange={(e) => setForm({ ...form, module_title: e.target.value })} data-testid="module-title-input" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={save} data-testid="module-save-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 ? <div className="text-slate-500 col-span-full">No modules yet.</div> :
          items.map((m) => (
            <Card key={m.id} className="p-6 border-slate-200 hover:shadow-md transition-shadow" data-testid={`module-${m.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-blue-50 text-blue-700 grid place-items-center"><FileText className="w-5 h-5" strokeWidth={1.5} /></div>
                <Badge className={m.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-slate-200"}>{m.status}</Badge>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold">{m.module_title}</h3>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">{m.description}</p>
              <div className="mt-5 flex items-center justify-between">
                <Link to={`/admin/modules/${m.id}/questions`} className="text-sm font-semibold text-blue-700 inline-flex items-center gap-1 hover:underline" data-testid={`view-questions-${m.id}`}>
                  Questions <ArrowRight className="w-4 h-4" />
                </Link>
                <div className="flex gap-2">
                  {hasPerm(user, "module", "edit") && <Button size="icon" variant="outline" onClick={() => { setEditing(m); setForm({ module_title: m.module_title, description: m.description || "", status: m.status }); setOpen(true); }}><Edit className="w-4 h-4" /></Button>}
                  {hasPerm(user, "module", "delete") && <Button size="icon" variant="outline" className="text-red-600" onClick={() => del(m.id)}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            </Card>
          ))}
      </div>

      <Dialog open={deleteDlg.open} onOpenChange={(v) => { if (!deleteDlg.busy) setDeleteDlg((d) => ({ ...d, open: v })); }}>
        <DialogContent className="max-w-md" data-testid="delete-module-dialog">
          <DialogHeader>
            <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-rose-100 dark:bg-rose-500/15 grid place-items-center">
              <AlertTriangle className="w-6 h-6 text-rose-600 dark:text-rose-400" />
            </div>
            <DialogTitle className="font-display text-center text-xl">Delete this module?</DialogTitle>
            <DialogDescription className="text-center">
              <span className="font-semibold text-slate-900 dark:text-slate-100">"{deleteDlg.title}"</span> and ALL its questions + AI explanations will be permanently removed.{" "}
              <span className="font-semibold text-rose-700 dark:text-rose-300">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              className="w-full sm:w-auto h-10"
              onClick={() => setDeleteDlg({ open: false, id: null, title: "", busy: false })}
              disabled={deleteDlg.busy}
              data-testid="delete-module-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="w-full sm:w-auto h-10 bg-rose-600 hover:bg-rose-700 text-white"
              disabled={deleteDlg.busy}
              onClick={() => del(deleteDlg.id)}
              data-testid="delete-module-confirm-btn"
            >
              {deleteDlg.busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              Delete module
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
